import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Dialog as DialogPrimitive } from 'radix-ui'
import { IconButton } from '@/components/icon-button/IconButton'
import { XClose } from '@/assets/icons'

export type ModalHeaderProps = {
  /** Title of the modal */
  title?: string
  /** Subtitle of the modal */
  subtitle?: string
  /** Optional classes for subtitle variants defined by a consuming modal. */
  subtitleClassName?: string
  /** Alignment of the title. Default: 'left' */
  titleAlign?: 'left' | 'center'
  /** Small icon rendered inline before the title text. Default: undefined */
  titleIcon?: React.ReactNode
  /** Show the close button in the header. Default: false */
  showCloseButton?: boolean
  /** Control rendered on the title row, filling the space after the title (e.g. a search field). */
  titleTrailing?: React.ReactNode
}

/**
 * Title, optional subtitle, and optional close control — shared by standard and tabbed modals.
 */
export function ModalHeader({ title, subtitle, subtitleClassName, titleAlign = 'left', titleIcon, showCloseButton = false, titleTrailing }: ModalHeaderProps) {
  if (!title && !subtitle && !showCloseButton && !titleTrailing) return null

  const titleNode = title && (
    <DialogPrimitive.Title
      className={twMerge(
        'flex items-center gap-x-2 font-rethink-sans text-xl font-semibold leading-7 tracking-normal text-text-primary',
        titleAlign === 'left' && 'justify-start',
        titleAlign === 'center' && 'justify-center',
        // The trailing control keeps its own clearance from the close button, so the title only
        // needs the reserved space when it is the last thing on the row.
        showCloseButton && !titleTrailing && 'pr-6'
      )}
    >
      {titleIcon && <span className="shrink-0 self-start">{titleIcon}</span>}
      {title}
    </DialogPrimitive.Title>
  )

  return (
    <div className={showCloseButton ? 'relative' : undefined}>
      {titleTrailing ? (
        <div className={twMerge('flex items-center gap-6', showCloseButton && 'pr-8')}>
          <span className="shrink-0">{titleNode}</span>
          <div className="min-w-0 flex-1">{titleTrailing}</div>
        </div>
      ) : (
        titleNode
      )}
      {subtitle && (
        <DialogPrimitive.Description
          className={twMerge(
            'font-sans text-base font-normal leading-6 tracking-[-0.32px] text-text-primary mt-1.5',
            titleAlign === 'left' && 'text-left',
            titleAlign === 'center' && 'text-center',
            showCloseButton && 'pr-6',
            subtitleClassName
          )}
        >
          {subtitle}
        </DialogPrimitive.Description>
      )}
      {showCloseButton && (
        // -right-1 -top-1 offsets the 36px wrapper div by 4px so the smaller centered hover circle lands flush at the corner.
        <DialogPrimitive.Close asChild>
          <IconButton aria-label="Close" color="neutral" icon={<XClose />} size="md" variant="plain" wrapperClassName="absolute -right-1 -top-1" />
        </DialogPrimitive.Close>
      )}
    </div>
  )
}
